import { useEffect, useState } from 'react';

/*
 * WAI-ARIA tabs keyboard model with manual activation for tablists that are
 * not Bootstrap tabs: one Tab stop (the selected tab), Left/Right and
 * Home/End move focus between tabs, Enter/Space (native button click)
 * selects. Returns a callback ref for the role="tablist" element.
 */

const getTabs = (list) =>
  Array.from(list.querySelectorAll('[role="tab"]')).filter(
    (tab) => !tab.disabled && tab.closest('[role="tablist"]') === list
  );

const useTablistKeyboardNav = () => {
  const [list, setList] = useState(null);

  useEffect(() => {
    if (!list) return undefined;

    const setTabStop = (preferFocused) => {
      const tabs = getTabs(list);
      if (tabs.length === 0) return;
      const stop =
        (preferFocused && tabs.find((tab) => tab === document.activeElement)) ||
        tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') ||
        tabs[0];
      tabs.forEach((tab) => tab.setAttribute('tabindex', tab === stop ? '0' : '-1'));
    };
    const syncTabStop = () => setTabStop(true);
    const resetWhenFocusLeaves = (event) => {
      if (!list.contains(event.relatedTarget)) setTabStop(false);
    };

    const handleKeyDown = (event) => {
      const tabs = getTabs(list);
      const index = tabs.indexOf(event.target);
      if (index < 0) return;
      let next;
      switch (event.key) {
        case 'ArrowRight':
          next = tabs[(index + 1) % tabs.length];
          break;
        case 'ArrowLeft':
          next = tabs[(index - 1 + tabs.length) % tabs.length];
          break;
        case 'Home':
          next = tabs[0];
          break;
        case 'End':
          next = tabs[tabs.length - 1];
          break;
        default:
          return;
      }
      event.preventDefault();
      next.focus();
    };

    syncTabStop();
    list.addEventListener('keydown', handleKeyDown);
    list.addEventListener('focusin', syncTabStop);
    list.addEventListener('focusout', resetWhenFocusLeaves);
    const observer = new MutationObserver(syncTabStop);
    observer.observe(list, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-selected'] });

    return () => {
      list.removeEventListener('keydown', handleKeyDown);
      list.removeEventListener('focusin', syncTabStop);
      list.removeEventListener('focusout', resetWhenFocusLeaves);
      observer.disconnect();
    };
  }, [list]);

  return setList;
};

export default useTablistKeyboardNav;
